import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback } from 'react';

import type { EmojiMartEmoji } from '@trello/emoji';

import { EmojiProvider } from './EmojiProvider';
import { EmojiProviderComponent } from './EmojiProviderComponent';

export interface EmojiProps {
  emoji: string;
  onClick?: (emoji: EmojiMartEmoji, e: ReactMouseEvent) => void;
  onLeave?: (emoji: EmojiMartEmoji, e: ReactMouseEvent) => void;
  onOver?: (emoji: EmojiMartEmoji, e: ReactMouseEvent) => void;
  set?: 'native' | 'twitter';
  size?: number;
}

export const Emoji = ({
  emoji,
  onClick,
  onLeave,
  onOver,
  set = 'twitter',
  size = 16,
}: EmojiProps) => {
  const handleClick = useCallback(
    (emojiData: EmojiMartEmoji, e: ReactMouseEvent) => {
      onClick?.(emojiData, e);
    },
    [onClick],
  );

  const handleHover = useCallback(
    (emojiData: EmojiMartEmoji, e: ReactMouseEvent) => {
      onOver?.(emojiData, e);
    },
    [onOver],
  );

  const handleLeave = useCallback(
    (emojiData: EmojiMartEmoji, e: ReactMouseEvent) => {
      onLeave?.(emojiData, e);
    },
    [onLeave],
  );

  return (
    <EmojiProviderComponent isSmallSpinner>
      {(sheet, getBackgroundImageFn) => {
        const spriteSheetUrl = getBackgroundImageFn
          ? getBackgroundImageFn('twitter')
          : '';

        const emojiData = EmojiProvider.getEmojiDataSync(emoji);
        if (!emojiData) {
          // We shouldn't get here if data is already loaded, fail gracefully
          // with the aria label as the emoji shortcode passed in
          return <span aria-label={emoji} className="emoji-mart-emoji" />;
        }

        const nativeEmoji = spriteSheetUrl === '' ? emojiData.native : '';
        const emojiStyle =
          nativeEmoji === ''
            ? {
                display: 'inline-block',
                width: `${size}px`,
                height: `${size}px`,
                backgroundImage: `url(${spriteSheetUrl})`,
                backgroundSize: `${100 * sheet.cols}% ${100 * sheet.rows}%`,
                backgroundPosition: `${
                  (100 / (sheet.cols - 1)) * emojiData.x!
                }% ${(100 / (sheet.rows - 1)) * emojiData.y!}%`,
              }
            : {
                display: 'block',
                lineHeight: `${size}px`,
              };

        // currently renders button tag for comment reactions, span for notification reactions
        return onClick ? (
          <button
            aria-label={emojiData.label}
            className="emoji-mart-emoji"
            type="button"
            onClick={(e) => handleClick(emojiData, e)}
            onMouseEnter={(e) => handleHover(emojiData, e)}
            onMouseLeave={(e) => handleLeave(emojiData, e)}
          >
            <span style={emojiStyle}>{nativeEmoji}</span>
          </button>
        ) : (
          <span aria-label={emojiData.label} className="emoji-mart-emoji">
            <span style={emojiStyle}>{nativeEmoji}</span>
          </span>
        );
      }}
    </EmojiProviderComponent>
  );
};
